/* ---------------------------------------------------------------------------------------------------------------------
   LENUMA REVIEWS
   ---------------------------------------------------------------------------------------------------------------------
   Steuert nur Pfeile und Punkte. Das eigentliche Scrollen macht der Browser
   ueber scroll-snap, damit Wischen auf dem Handy sich nativ anfuehlt.
   Faellt das Script aus, bleibt das Karussell per Wischen und Tastatur nutzbar.
   --------------------------------------------------------------------------------------------------------------------- */

class LenumaReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.lenuma-reviews__track');
    this.cards = Array.from(this.querySelectorAll('.lenuma-reviews__card'));
    this.arrows = Array.from(this.querySelectorAll('.lenuma-reviews__arrow'));
    this.dotsContainer = this.querySelector('.lenuma-reviews__dots');

    if (!this.track || this.cards.length === 0) return;

    this.arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => this.scrollByPage(arrow.dataset.direction === 'next' ? 1 : -1));
    });

    this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

    // Bei Groessenaenderung neu bewerten: aus drei Karten koennen mobil eine werden
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.track);

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        event.target.scrollIntoView({ block: 'nearest', inline: 'center' });
      });
    }

    this.buildDots();
    this.refresh();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /** Ist ueberhaupt etwas zu scrollen? Bei wenigen Karten nicht. */
  get isScrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 2;
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  scrollByPage(direction) {
    const card = this.cards[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.textContent = '';

    this.dots = this.cards.map((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lenuma-reviews__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Bewertung ${index + 1} von ${this.cards.length}`);
      dot.addEventListener('click', () => {
        card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      });
      this.dotsContainer.appendChild(dot);
      return dot;
    });
  }

  /** Die Karte, die der Mitte des Sichtfensters am naechsten liegt. */
  get activeIndex() {
    const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const center = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(center - viewportCenter);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateState();
    });
  }

  updateState() {
    const scrollable = this.isScrollable;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= this.maxScroll - 1;

    this.arrows.forEach((arrow) => {
      arrow.hidden = !scrollable;
      arrow.disabled = arrow.dataset.direction === 'next' ? atEnd : atStart;
    });

    if (this.dotsContainer) {
      this.dotsContainer.hidden = !scrollable;
      const active = this.activeIndex;
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-selected', index === active ? 'true' : 'false');
      });
    }
  }

  refresh() {
    this.updateState();
  }
}

if (!window.customElements.get('lenuma-reviews')) {
  window.customElements.define('lenuma-reviews', LenumaReviews);
}
